import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import Auth from '@aws-amplify/auth';
import { exhaustMap, from, map, Observable } from 'rxjs';

import { ImageUploadResponse } from '@image-upload/interfaces/image-upload-response.interface';
import { ApiRoutes } from '@shared/constants/api-routes';

@Injectable({
  providedIn: 'root',
})
export class MerchantMarketingDataService {
  constructor(private httpClient: HttpClient) {}

  /***
     NOTE: as of 2022.11.30., I am unable to test the marketing profile image
     related calls due to a backend related issue (400 error response even with correct
     upload data). A possible solution for testing this feature is to swap the
     marketing profile picture upload API routes with the existing routes
     defined within the product service (container-images/package-images) since
     these have almost identical signatures.
     You also have to fetch the images from the product API instead of the current
     routes, and swap the deletion endpoints as well.

     Feel free to delete this note once the related error gets resolved.
     ***/

  deleteMerchantProfileImage(
    pictureId: string
  ): Observable<HttpResponse<null>> {
    return from(Auth.currentSession()).pipe(
      map(
        (s) =>
          (s.getIdToken().payload['custom:impMerchantId'] ??
            s.getIdToken().payload['custom:merchantId']) as string
      ),
      exhaustMap((merchantId: string) =>
        this.httpClient.delete<HttpResponse<null>>(
          `${ApiRoutes.deleteMerchantProfileImage(merchantId, pictureId)}`
        )
      )
    );
  }

  getMerchantProfileImage(pictureId: string): Observable<ImageUploadResponse> {
    return from(Auth.currentSession()).pipe(
      map(
        (s) =>
          (s.getIdToken().payload['custom:impMerchantId'] ??
            s.getIdToken().payload['custom:merchantId']) as string
      ),
      exhaustMap((merchantId: string) =>
        this.httpClient.get<ImageUploadResponse>(
          `${ApiRoutes.getMerchantProfileImage(merchantId, pictureId)}`
        )
      )
    );
  }

  uploadMerchantProfileImage(
    formData: FormData
  ): Observable<ImageUploadResponse[]> {
    return from(Auth.currentSession()).pipe(
      map(
        (s) =>
          (s.getIdToken().payload['custom:impMerchantId'] ??
            s.getIdToken().payload['custom:merchantId']) as string
      ),
      exhaustMap((merchantId: string) => {
        formData.set('path', `merchants/${merchantId}/pictures`);

        return this.httpClient.post<ImageUploadResponse[]>(
          `${ApiRoutes.uploadMerchantProfileImage(merchantId)}`,
          formData
        );
      })
    );
  }
}
